/* Кнопки статусов карточки заказа: переходы по потоку вертикали, отмена/неявка с причиной, «Выдать и закрыть» с оплатой. */
import { ACTION, HOUR, STATUS, money, nextStatuses, totals, type Order, type OrderStatus } from "@okno/shared";
import { ApiError, api } from "../../lib/api";
import { useActions } from "../../lib/actions";
import { useSession } from "../../lib/session";
import { Icon } from "../../ui/Icon";
import { useConfirm } from "../../ui/layers";
import { useToast } from "../../ui/toast";
import { usePaymentModal } from "./PaymentsSection";
import { useOrderApi } from "./useOrder";

const NEGATIVE: OrderStatus[] = ["cancelled", "no_show"];
/** Статусы, которые исполнитель двигает сам в своих заказах (как на сервере). */
const WORKER_STATUSES: OrderStatus[] = ["in_work", "ready", "diagnostics", "approval", "parts"];

const CANCEL_REASONS = ["Клиент перенёс", "Клиент передумал", "Не успевает", "Нет свободного мастера", "Другое"];
const NO_SHOW_REASONS = ["Не приехал в окно 15 минут", "Не отвечает на звонки", "Другое"];

type StatusResult = { order: Order; refunded: number };

export function OrderFoot({ order: o }: { order: Order }) {
  const { boot, vertical, can, me } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const { openBooking } = useActions();
  const { path, run } = useOrderApi(o.id);
  const openPayment = usePaymentModal(o.id);

  const all = nextStatuses(o, vertical);
  const next = can("booking") ? all : o.workerId === me.id ? all.filter((s) => WORKER_STATUSES.includes(s)) : [];
  const positive = next.filter((s) => !NEGATIVE.includes(s));
  const negative = next.filter((s) => NEGATIVE.includes(s));

  /** Закрыть заказ; сервер может ответить payment_due — тогда сначала оплата, после неё повтор. */
  const close = async () => {
    try {
      const res = await api.post<StatusResult>(path + "/status", { status: "done" });
      await run(() => Promise.resolve(res));
      toast(STATUS.done.label);
    } catch (e) {
      if (e instanceof ApiError && e.code === "payment_due") {
        toast(e.message, "bad");
        openPayment(() => void close());
      } else toast.error(e);
    }
  };

  const askReason = (next: OrderStatus) => {
    const t = totals(o, boot.settings);
    const deposit = Math.max(0, t.paid);
    const hoursLeft = (o.start - Date.now()) / HOUR;
    const win = boot.settings.refundWindowH;
    let rule = "";
    if (deposit > 0) {
      rule =
        next === "no_show"
          ? "Депозит " + money(deposit) + " не возвращается — неявка."
          : hoursLeft >= win
            ? "До визита больше " + win + " ч — депозит " + money(deposit) + " вернётся клиенту."
            : "До визита меньше " + win + " ч — депозит " + money(deposit) + " остаётся у точки.";
    }
    return confirm({
      title: next === "cancelled" ? "Отменить запись?" : "Отметить неявку?",
      text: (
        <>
          Слот освободится и станет доступен для продажи.
          {rule ? (
            <>
              <br />
              <br />
              {rule}
            </>
          ) : null}
        </>
      ),
      ok: next === "cancelled" ? "Отменить запись" : "Отметить неявку",
      danger: true,
      reasons: { label: "Причина", options: next === "cancelled" ? CANCEL_REASONS : NO_SHOW_REASONS },
    });
  };

  const change = async (next: OrderStatus) => {
    if (next === "done") {
      const t = totals(o, boot.settings);
      if (t.due > 0) {
        toast("Сначала примите оплату: осталось " + money(t.due), "bad");
        openPayment(() => void close());
        return;
      }
      return close();
    }
    if (NEGATIVE.includes(next)) {
      const reason = await askReason(next);
      if (typeof reason !== "string") return;
      const res = await run(() => api.post<StatusResult>(path + "/status", { status: next, reason }));
      if (!res) return;
      const msg = next === "cancelled" ? "Запись отменена, слот свободен" : "Неявка отмечена";
      toast(res.refunded > 0 ? msg + " · возврат " + money(res.refunded) : msg);
      return;
    }
    const res = await run(() => api.post<StatusResult>(path + "/status", { status: next }));
    if (res) toast(STATUS[next].label);
  };

  if (!all.length) {
    return (
      <>
        <span className="muted" style={{ alignSelf: "center" }}>
          Заказ закрыт — изменения недоступны
        </span>
        <span style={{ flex: 1 }} />
        {can("booking") ? (
          <button className="btn" onClick={() => openBooking({ customerId: o.customerId, vehicleId: o.vehicleId, serviceIds: o.items.map((x) => x.sid) })}>
            <Icon name="refresh" size={16} />
            Записать снова
          </button>
        ) : null}
      </>
    );
  }
  if (!next.length) return null;
  return (
    <>
      {negative.map((s) => (
        <button key={s} className="btn btn-danger" onClick={() => void change(s)}>
          {ACTION[s]}
        </button>
      ))}
      <span style={{ flex: 1 }} />
      {positive.map((s, i) => (
        <button key={s} className={"btn" + (i === 0 ? " btn-primary" : "")} onClick={() => void change(s)}>
          {ACTION[s]}
        </button>
      ))}
    </>
  );
}
